import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const TicketAIAgentModal = ({ open, onClose, ticket, ticketId }) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  // Default TRUE: com atendente humano vinculado a IA não responde (regra do listener),
  // então transferir para o bot precisa soltar o humano para surtir efeito
  const [clearHumanAttendant, setClearHumanAttendant] = useState(true);
  const [loadedPromptId, setLoadedPromptId] = useState(null);

  const ticketPromptId = ticket?.promptId ?? loadedPromptId ?? null;
  const resolvedTicketId = ticket?.id ?? ticketId;

  const currentAgentLabel = useMemo(() => {
    if (!ticketPromptId) return i18n.t("ticketAIAgentModal.none");

    const found = agents.find((agent) => Number(agent.id) === Number(ticketPromptId));
    if (!found) return i18n.t("ticketAIAgentModal.none");

    return found.name;
  }, [agents, ticketPromptId]);

  useEffect(() => {
    if (!open) {
      setSelectedAgent(null);
      setClearHumanAttendant(true);
      setAgents([]);
      setLoadedPromptId(null);
      return;
    }

    let cancelled = false;

    const loadAgents = async () => {
      setLoading(true);
      try {
        let page = 1;
        let hasMore = true;
        const allPrompts = [];

        while (hasMore) {
          const { data } = await api.get("/prompt", {
            params: { pageNumber: page },
          });

          const pagePrompts = Array.isArray(data?.prompts) ? data.prompts : [];
          allPrompts.push(...pagePrompts);
          hasMore = Boolean(data?.hasMore);
          page += 1;
        }

        const filteredAgents = allPrompts.filter((prompt) => {
          const aiHubId = String(prompt?.aiHubAgentId || "").trim();
          return Boolean(prompt?.aiHubEnabled || aiHubId);
        });

        const finalAgents = filteredAgents.length > 0 ? filteredAgents : allPrompts;

        if (!cancelled) {
          setAgents(finalAgents);

          if (ticketPromptId) {
            const selected = finalAgents.find((agent) => Number(agent.id) === Number(ticketPromptId)) || null;
            setSelectedAgent(selected);
          }
        }
      } catch (err) {
        if (!cancelled) {
          toastError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAgents();

    return () => {
      cancelled = true;
    };
  }, [open, ticketPromptId]);

  useEffect(() => {
    if (!open) return;
    if (ticket?.promptId !== undefined && ticket?.promptId !== null) return;
    if (!resolvedTicketId || Number.isNaN(Number(resolvedTicketId))) return;

    let cancelled = false;

    const loadTicket = async () => {
      try {
        const { data } = await api.get(`/tickets/${Number(resolvedTicketId)}`);
        if (!cancelled) {
          setLoadedPromptId(data?.promptId ?? null);
        }
      } catch (_err) {
        if (!cancelled) {
          setLoadedPromptId(null);
        }
      }
    };

    loadTicket();

    return () => {
      cancelled = true;
    };
  }, [open, resolvedTicketId, ticket?.promptId]);

  const handleAssignAgent = async () => {
    if (!resolvedTicketId || Number.isNaN(Number(resolvedTicketId))) {
      toast.error(i18n.t("ticketAIAgentModal.errors.invalidTicket"));
      return;
    }

    if (!selectedAgent?.id) {
      toast.error(i18n.t("ticketAIAgentModal.errors.selectAgent"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        promptId: selectedAgent.id,
        transferredBy: user?.id,
      };

      if (clearHumanAttendant) {
        // Devolve o chamado ao "Aguardando" sem dono — estado em que a IA responde.
        // (status pending zera o userId no backend)
        payload.status = "pending";
        payload.userId = null;
      }

      await api.put(`/tickets/${Number(resolvedTicketId)}`, payload);
      toast.success(i18n.t("ticketAIAgentModal.success.assigned"));
      if (!clearHumanAttendant) {
        toast.warn(i18n.t("ticketAIAgentModal.warnings.humanAttendantLinked"));
      }
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAgent = async () => {
    if (!resolvedTicketId || Number.isNaN(Number(resolvedTicketId))) {
      toast.error(i18n.t("ticketAIAgentModal.errors.invalidTicket"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        promptId: null,
      };

      if (clearHumanAttendant) {
        payload.userId = null;
      }

      await api.put(`/tickets/${Number(resolvedTicketId)}`, payload);
      toast.success(i18n.t("ticketAIAgentModal.success.removed"));
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{i18n.t("ticketAIAgentModal.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          {i18n.t("ticketAIAgentModal.currentAgent", { agent: currentAgentLabel })}
        </Typography>

        <Autocomplete
          options={agents}
          value={selectedAgent}
          loading={loading}
          getOptionLabel={(option) => option?.name || ""}
          getOptionSelected={(option, value) => option?.id === value?.id}
          onChange={(_event, newValue) => setSelectedAgent(newValue)}
          noOptionsText={i18n.t("ticketAIAgentModal.noOptions")}
          renderOption={(option) => option?.name || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={i18n.t("ticketAIAgentModal.searchLabel")}
              placeholder={i18n.t("ticketAIAgentModal.searchPlaceholder")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {/*
          Frontend decision: keep current human attendant by default.
          This option is intentionally hidden because it is not used.
        */}
        {/* <FormControlLabel
          style={{ marginTop: 12 }}
          control={
            <Checkbox
              color="primary"
              checked={clearHumanAttendant}
              onChange={(event) => setClearHumanAttendant(event.target.checked)}
            />
          }
          label={i18n.t("ticketAIAgentModal.clearHumanAttendant")}
        /> */}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {i18n.t("ticketAIAgentModal.buttons.cancel")}
        </Button>
        {/*
          Frontend decision: remove action hidden because it is not used.
        */}
        {/* <Button onClick={handleRemoveAgent} disabled={saving}>
          {i18n.t("ticketAIAgentModal.buttons.remove")}
        </Button> */}
        <Button
          color="primary"
          variant="contained"
          onClick={handleAssignAgent}
          disabled={saving || !selectedAgent}
        >
          {i18n.t("ticketAIAgentModal.buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketAIAgentModal;
